// Systems.tsx
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  Button,
  Drawer,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { alpha } from "@mui/material/styles";
import AccordionPanel from "../components/system/AccordionPanel";
import ConfigDetails from "../components/system/ConfigDetails";
import SystemDetails from "../components/system/SystemDetails";
import SystemSettingsForm from "../components/system/forms/SystemSettingsForm";
import { SystemsProvider, useSystemsContext } from "../context/SystemsContext";
import { subscribeToSystem, describeRealtimeEvent } from "../services/realtime";
import { useRealtimeToast } from "../context/RealtimeToastContext";
import SystemActionButtons from "../components/system/SystemActionButtons";
import { useSession } from "../context/SessionContext";
import SystemJsonEditor from "../components/system/SystemJsonEditor";
import ServiceDefinitionsManager from "../components/system/ServiceDefinitionsManager";
import SystemHardware from "../components/system/SystemHardware";

const formatSectionLabel = (section?: string | null) => {
  if (!section) return "System Info";

  const labels: Record<string, string> = {
    systemInfo: "System Info",
    systemHardware: "System Hardware",
    serviceDefinitions: "Service Definitions",
    createSystem: "Create System",
    clientProcess: "Client Process",
    assetProcessing: "Asset Processing",
    assetSyndication: "Asset Syndication",
    assetStorage: "Asset Storage",
  };

  return labels[section] || section;
};

// This new component decides which main view to show
const MainContentPanel: React.FC = () => {
  const { isLoadingSystem, selectedSection, selectedConfig: expandedConfigId, configs } = useSystemsContext();
  const { user } = useSession();
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isDark = theme.palette.mode === "dark";
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const selectedConfig = configs.find((c: any) => c.configKey === expandedConfigId);
  const configSectionData = selectedConfig?.[selectedSection || ""];
  const isAdmin = user?.role === "Admin";
  const canUseJsonEditor =
    isAdmin &&
    selectedSection !== "createSystem" &&
    selectedSection !== "systemHardware" &&
    selectedSection !== "serviceDefinitions";

  if (isLoadingSystem) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  if (selectedSection === "createSystem") {
    return <SystemSettingsForm />;
  }
  if (selectedSection === "serviceDefinitions") {
    if (!isAdmin) {
      return (
        <Typography variant="body1" sx={{ mt: 4 }}>
          You do not have permission to view Service Definitions.
        </Typography>
      );
    }
    return <ServiceDefinitionsManager />;
  }
  if (selectedSection === "systemHardware") {
    return <SystemHardware />;
  }

  if (showJsonEditor && canUseJsonEditor) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={showJsonEditor ? "json" : "form"}
            onChange={(_, mode) => {
              if (!mode) return;
              setShowJsonEditor(mode === "json");
            }}
            sx={{
              backgroundColor: alpha(isDark ? "#fff" : "#000", 0.03),
              borderRadius: "10px",
              p: 0.5,
              gap: 0.5,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "13px",
                letterSpacing: 0,
                border: "none",
                borderRadius: "8px !important",
                px: 1.75,
                py: 0.5,
                minHeight: 0,
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: alpha(isDark ? "#fff" : "#000", 0.04),
                },
                "&.Mui-selected": {
                  backgroundColor: isDark ? "#1d1d20" : "#fff",
                  color: "text.primary",
                  fontWeight: 600,
                  boxShadow: isDark
                    ? `0 1px 0 ${alpha("#fff", 0.08)} inset, 0 1px 3px rgba(0,0,0,0.4)`
                    : "0 1px 2px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.06)",
                  "&:hover": {
                    backgroundColor: isDark ? "#23232a" : "#fff",
                  },
                },
              },
            }}
          >
            <ToggleButton data-cy="systems-toggle-form-btn" value="form">Form</ToggleButton>
            <ToggleButton data-cy="systems-toggle-json-btn" value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <SystemJsonEditor />
      </Box>
    );
  }

  if (canUseJsonEditor) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ToggleButtonGroup
            exclusive
            value={showJsonEditor ? "json" : "form"}
            onChange={(_, mode) => {
              if (!mode) return;
              setShowJsonEditor(mode === "json");
            }}
            sx={{
              backgroundColor: alpha(isDark ? "#fff" : "#000", 0.03),
              borderRadius: "10px",
              p: 0.5,
              gap: 0.5,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "13px",
                letterSpacing: 0,
                border: "none",
                borderRadius: "8px !important",
                px: 1.75,
                py: 0.5,
                minHeight: 0,
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: alpha(isDark ? "#fff" : "#000", 0.04),
                },
                "&.Mui-selected": {
                  backgroundColor: isDark ? "#1d1d20" : "#fff",
                  color: "text.primary",
                  fontWeight: 600,
                  boxShadow: isDark
                    ? `0 1px 0 ${alpha("#fff", 0.08)} inset, 0 1px 3px rgba(0,0,0,0.4)`
                    : "0 1px 2px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.06)",
                  "&:hover": {
                    backgroundColor: isDark ? "#23232a" : "#fff",
                  },
                },
              },
            }}
          >
            <ToggleButton data-cy="systems-toggle-form-btn" value="form">Form</ToggleButton>
            <ToggleButton data-cy="systems-toggle-json-btn" value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {selectedSection === "systemInfo" ? (
          <SystemDetails />
        ) : configSectionData ? (
          <ConfigDetails />
        ) : (
          <Typography variant="body1" sx={{ mt: 4 }}>
            Select a section to view details
          </Typography>
        )}
      </Box>
    );
  }

  if (selectedSection === "systemInfo") {
    return <SystemDetails />;
  }
  if (configSectionData) {
    return <ConfigDetails />;
  }
  return <Typography variant="body1" sx={{ mt: 4 }}>Select a section to view details</Typography>;
};

const SystemsContent: React.FC = () => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { selectedSection, selectedConfig, configs, applyRemoteChanges } = useSystemsContext();
  const { refetchSystems, refetchSelectedSystem, selectedSystem, systems, selectSystem } = useSession();
  const { notify } = useRealtimeToast();
  const selectedSystemCode = selectedSystem?.systemCode;
  const [searchParams] = useSearchParams();
  const urlSystemAppliedRef = useRef(false);

  // Deep link: /systems?system=<code> pre-selects that system. Units link here
  // from their local admin UI so an operator lands on the right record instead
  // of hunting the list. Mirrors the same param on /assets.
  //
  // Applied once — after the first pass the user owns the selection, so a later
  // manual switch isn't yanked back by the stale param still in the URL.
  useEffect(() => {
    if (urlSystemAppliedRef.current) return;
    if (!systems.length) return;

    const urlSystemCode = searchParams.get("system");
    if (!urlSystemCode) {
      urlSystemAppliedRef.current = true;
      return;
    }

    // Units know themselves by hostname (…S01U01); the system record is keyed
    // without the unit suffix. Strip it so either form resolves.
    const normalizedUrlCode = urlSystemCode.replace(/U\d{2}$/, "");
    const target = systems.find((system) => system.systemCode === normalizedUrlCode) || null;

    if (target && target.systemCode !== selectedSystem?.systemCode) {
      selectSystem(target);
    }

    urlSystemAppliedRef.current = true;
  }, [searchParams, selectedSystem?.systemCode, selectSystem, systems]);

  // The full list is loaded once at boot (and cached for fast switching). On
  // selection we only revalidate the one system the user is viewing — a cheap
  // get_item, no spinner — then IoT (below) keeps it live. This avoids re-running
  // the expensive full table scan every time we land here or switch systems.
  useEffect(() => {
    if (!selectedSystemCode) return;
    refetchSelectedSystem(selectedSystemCode);
  }, [selectedSystemCode, refetchSelectedSystem]);

  // Subscribe to real-time change events for the currently-selected system over
  // AWS IoT Core. Any message published under systems/<code>/# triggers a
  // refetch, so an edit made elsewhere shows up live without a manual refresh.
  // Re-runs when the selection changes: tears down the old subscription and
  // opens one for the newly-selected system.
  useEffect(() => {
    if (!selectedSystemCode) return;
    let sub: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    subscribeToSystem(
      selectedSystemCode,
      (data: any) => {
        console.log("[realtime] change event:", data);
        // Unit -> UMS run acknowledgement — just toast it; it's not a system-config
        // change, so don't apply diffs or refetch systems.
        if (data && data.entity === "runStatus") {
          const msg = describeRealtimeEvent(data);
          if (msg) notify(msg);
          return;
        }
        // Small, self-contained config edits ship the field diff — apply it
        // directly (preserving the local user's in-progress fields). Camera
        // edits and oversized diffs set refetch=true (server-side renumber
        // cascade etc.) — pull the authoritative record instead.
        if (data && data.changes && !data.refetch) {
          applyRemoteChanges(data.changes, data.removePaths || []);
        } else {
          refetchSystems();
        }
        notify(describeRealtimeEvent(data));
      },
      (err) => console.error("[realtime] subscription error:", err)
    )
      .then((s) => {
        if (cancelled) s.unsubscribe();
        else sub = s;
      })
      .catch((err) => console.error("[realtime] subscribe failed:", err));
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [selectedSystemCode, refetchSystems, applyRemoteChanges, notify]);
  const selectedConfigRecord = configs.find((config: any) => config.configKey === selectedConfig);
  const currentLabel = selectedConfig && selectedSection !== "systemInfo" && selectedSection !== "systemHardware" && selectedSection !== "createSystem" && selectedSection !== "serviceDefinitions"
    ? `${selectedConfigRecord?.clientProcess?.name || selectedConfigRecord?.name || selectedConfig}: ${formatSectionLabel(selectedSection)}`
    : formatSectionLabel(selectedSection);

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
      {isMobile ? (
        <>
          <Box
            sx={{
              p: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Button
              data-cy="systems-mobile-nav-btn"
              variant="outlined"
              fullWidth
              startIcon={<TuneIcon />}
              onClick={() => setMobileNavOpen(true)}
              sx={{
                justifyContent: "space-between",
                py: 1.25,
                textTransform: "none",
                fontWeight: 600,
                // The theme's primary color is pure black in dark mode, so the
                // default outlined button renders black-on-black ("just a black
                // bar"). Drive the text, icon, and border off readable tokens.
                color: "text.primary",
                borderColor: theme.palette.divider,
                "& .MuiButton-startIcon": { color: "text.secondary" },
                "&:hover": {
                  borderColor: alpha(accent, 0.6),
                  backgroundColor: alpha(accent, isDark ? 0.12 : 0.08),
                },
              }}
            >
              {currentLabel}
            </Button>
          </Box>
          <Drawer
            anchor="top"
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            PaperProps={{
              sx: {
                maxHeight: "75vh",
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              },
            }}
          >
            <AccordionPanel mobile onNavigate={() => setMobileNavOpen(false)} />
          </Drawer>
        </>
      ) : (
        <AccordionPanel />
      )}

      <Box sx={{ flex: 1, p: isMobile ? 2 : 3, overflow: "auto" }}>
        <MainContentPanel />
        <SystemActionButtons />
      </Box>
    </Box>

  );
};

const Systems: React.FC = () => {
  return (
    <SystemsProvider>
      <SystemsContent />
    </SystemsProvider>
  );
};



export default Systems;
